// src/components/register.js
import React, { useState, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ASSISTANT_AUTH_REGISTER_URL } from '../constants';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError(null);
  };

  // Validaciones rápidas
  const passwordsFilled = formData.password.length > 0 && formData.confirmPassword.length > 0;
  const passwordsMatch = useMemo(
    () => formData.password === formData.confirmPassword,
    [formData.password, formData.confirmPassword]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.username || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('Todos los campos son obligatorios');
      return;
    }
    if (!passwordsMatch) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (formData.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
      };

      if (!ASSISTANT_AUTH_REGISTER_URL || ASSISTANT_AUTH_REGISTER_URL.startsWith('undefined')) {
        console.error('[REGISTER] URL inválida:', ASSISTANT_AUTH_REGISTER_URL);
        setError('Configuración inválida del servidor. Revisa REACT_APP_ASSISTANT_URL.');
        setSubmitting(false);
        return;
      }

      const res = await axios.post(ASSISTANT_AUTH_REGISTER_URL, payload, {
        withCredentials: true,
        validateStatus: () => true, // permitir inspeccionar status
      });

      if (res.status >= 200 && res.status < 300 && res.data?.token) {
        const { token, username } = res.data;
        try {
          login(token, username);
        } catch {
          login(token);
          if (username) localStorage.setItem('username', username);
        }
        navigate('/admin'); // 🔹 Redirige siempre a /admin
        return;
      }

      if (res.status === 409) return setError('El correo ya está registrado.');
      if (res.status === 400) return setError(res.data?.msg || 'Faltan datos para el registro.');
      if (res.status >= 500) return setError('Error del servidor. Inténtalo más tarde.');
      setError(res.data?.msg || `Error HTTP ${res.status}`);
    } catch (err) {
      console.error('[REGISTER][EXC]', err.message, err.code);
      if (err.response) {
        setError(err.response?.data?.msg || `Error HTTP ${err.response?.status}`);
      } else {
        setError('No se pudo contactar al servidor. Revisa CORS, URL y conectividad.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    'mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm';

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white shadow-md rounded-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-900">Registro Admin UNIMEL</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Nombre de usuario</label>
            <input
              type="text"
              name="username"
              placeholder="Tu nombre de usuario"
              value={formData.username}
              onChange={handleChange}
              className={`${inputBase} border-gray-300`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Correo electrónico</label>
            <input
              type="email"
              name="email"
              placeholder="correo@ejemplo.com"
              value={formData.email}
              onChange={handleChange}
              autoComplete="username"
              className={`${inputBase} border-gray-300`}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Contraseña</label>
            <input
              type="password"
              name="password"
              placeholder="Tu contraseña"
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              className={`${inputBase} ${passwordsFilled && !passwordsMatch ? 'border-red-400' : 'border-gray-300'}`}
              required
              minLength={8}
            />
            <p className="mt-1 text-xs text-gray-500">Mínimo 8 caracteres.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Confirmar contraseña</label>
            <input
              type="password"
              name="confirmPassword"
              placeholder="Repite tu contraseña"
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              className={`${inputBase} ${passwordsFilled && !passwordsMatch ? 'border-red-400' : 'border-gray-300'}`}
              required
              minLength={8}
              aria-invalid={passwordsFilled && !passwordsMatch}
              aria-describedby={!passwordsMatch ? 'pwd-mismatch' : undefined}
            />
            {!passwordsMatch && passwordsFilled && (
              <p id="pwd-mismatch" className="mt-1 text-xs text-red-600">Las contraseñas no coinciden.</p>
            )}
          </div>

          {error && <p className="text-red-500 text-sm">{error}</p>}

          <button
            type="submit"
            disabled={submitting || (passwordsFilled && !passwordsMatch)}
            className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md shadow-sm hover:bg-indigo-700 disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {submitting ? 'Creando cuenta…' : 'Registrarse'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            ¿Ya tienes una cuenta?
            <Link to="/login" className="text-indigo-600 hover:underline"> Inicia sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
